import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Radio,
  Paper,
  RadioGroup,
  Stack,
  Chip,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Inventory2,
  Image as ImageIcon,
  Save,
  ArrowForward,
  ArrowBack,
  ShoppingBag,
  CheckCircleOutline,
  InboxOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import CategoryTreeView from '../Product/CategoryTreeView';
import { getProductImagesFromRow } from '../Product/ImagePreviewModal';
import ProductDetailModal from '../Product/ProductDetailModal';
import { fetchProductCategoryRequest } from '../../../redux/Product/ProductCategorySlice';
import FileUploader from '../../Comman/FileUploader';
import axios from 'axios';
import { toast } from 'react-toastify';
import { canShowPermission } from '../Permissions/permissionObject';
import { ACCENT, ACCENT_LIGHT, ACCENT_BORDER } from './categoryUiShared';

const sortByCreatedAt = (list) =>
  [...list].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';

const ProductListPanel = ({
  title,
  subtitle,
  count,
  emptyMessage,
  products,
  selectedId,
  onSelect,
  onView,
  variant = 'available',
}) => {
  const isIncluded = variant === 'included';
  const headerBg = isIncluded
    ? 'linear-gradient(135deg, #7028DA 0%, #9b59e8 100%)'
    : 'linear-gradient(135deg, #f5f5f7 0%, #ececf1 100%)';
  const headerColor = isIncluded ? '#fff' : 'text.primary';
  const subtitleColor = isIncluded ? 'rgba(255,255,255,0.85)' : 'text.secondary';
  const panelBorder = isIncluded ? ACCENT_BORDER : 'divider';
  const emptyIconBg = isIncluded ? ACCENT_LIGHT : 'grey.100';
  const emptyIconColor = isIncluded ? ACCENT : 'text.disabled';

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: selectedId ? ACCENT_BORDER : panelBorder,
        boxShadow: selectedId ? `0 0 0 1px ${ACCENT_BORDER}` : 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: headerBg,
          color: headerColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: isIncluded ? 'rgba(255,255,255,0.2)' : ACCENT_LIGHT,
              color: isIncluded ? '#fff' : ACCENT,
            }}
          >
            {isIncluded ? <CheckCircleOutline sx={{ fontSize: 20 }} /> : <ShoppingBag sx={{ fontSize: 20 }} />}
          </Avatar>
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: 'inherit' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" noWrap sx={{ color: subtitleColor, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <Badge
          badgeContent={count}
          showZero
          sx={{
            '& .MuiBadge-badge': {
              position: 'static',
              transform: 'none',
              fontWeight: 700,
              fontSize: 13,
              minWidth: 28,
              height: 28,
              borderRadius: '14px',
              bgcolor: isIncluded ? '#fff' : ACCENT,
              color: isIncluded ? ACCENT : '#fff',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          maxHeight: 280,
          overflowY: 'auto',
          bgcolor: '#fff',
          p: 1.25,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
        }}
      >
        {products.length > 0 ? (
          <Stack spacing={0.75}>
            {products.map((p) => {
              const isSelected = selectedId === p._id;
              const name = p?.name ?? 'N/A';
              const productImage = getProductImagesFromRow(p)[0];
              return (
                <ListItemButton
                  key={p._id}
                  selected={isSelected}
                  onClick={() => onSelect(p._id)}
                  sx={{
                    borderRadius: 2,
                    px: 1.25,
                    py: 1,
                    border: '1px solid',
                    borderColor: isSelected ? ACCENT : 'grey.200',
                    bgcolor: isSelected ? ACCENT_LIGHT : 'grey.50',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: isSelected ? 'rgba(112, 40, 218, 0.12)' : '#fff',
                      borderColor: isSelected ? ACCENT : ACCENT_BORDER,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(112, 40, 218, 0.08)',
                    },
                    '&.Mui-selected': {
                      bgcolor: ACCENT_LIGHT,
                      '&:hover': { bgcolor: 'rgba(112, 40, 218, 0.14)' },
                    },
                  }}
                >
                  <Avatar
                    src={productImage}
                    alt={name}
                    sx={{
                      width: 36,
                      height: 36,
                      mr: 1.25,
                      fontSize: 12,
                      fontWeight: 700,
                      bgcolor: isSelected ? ACCENT : isIncluded ? 'rgba(112, 40, 218, 0.15)' : 'grey.200',
                      color: isSelected || isIncluded ? (isSelected ? '#fff' : ACCENT) : 'text.secondary',
                      border: '1px solid',
                      borderColor: isSelected ? ACCENT : 'grey.200',
                      '& img': { objectFit: 'cover' },
                    }}
                  >
                    {getInitials(name)}
                  </Avatar>
                  <ListItemText
                    primary={name}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? ACCENT : 'text.primary',
                      noWrap: true,
                    }}
                  />
                  {isSelected && (
                    <Chip
                      label="Selected"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: ACCENT,
                        color: '#fff',
                        mr: 0.5,
                      }}
                    />
                  )}
                  {onView && (
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(p);
                        }}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                        }}
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemButton>
              );
            })}
          </Stack>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 5,
              px: 2,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'grey.300',
              bgcolor: 'grey.50',
            }}
          >
            <Avatar sx={{ width: 48, height: 48, mx: 'auto', mb: 1.5, bgcolor: emptyIconBg, color: emptyIconColor }}>
              {isIncluded ? <InboxOutlined /> : <Inventory2 />}
            </Avatar>
            <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
              {emptyMessage}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {isIncluded ? 'Use → to add products' : 'Use ← to remove products'}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const TransferControls = ({ onAdd, onRemove, canAdd, canRemove }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={0}
    sx={{
      flexShrink: 0,
      px: { xs: 0, sm: 0.5 },
      py: { xs: 1, sm: 0 },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: '#fff',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 4px 20px rgba(112, 40, 218, 0.1)',
        overflow: 'hidden',
        minWidth: 52,
      }}
    >
      <Tooltip title="Add to category">
        <span style={{ width: '100%' }}>
          <IconButton
            onClick={onAdd}
            disabled={!canAdd}
            sx={{
              width: 52,
              height: 48,
              borderRadius: 0,
              bgcolor: canAdd ? ACCENT : 'grey.100',
              color: canAdd ? '#fff' : 'grey.400',
              '&:hover': { bgcolor: canAdd ? '#5a1fb0' : 'grey.100' },
            }}
          >
            <ArrowForward />
          </IconButton>
        </span>
      </Tooltip>
      <Box sx={{ width: '70%', height: '1px', bgcolor: 'divider' }} />
      <Tooltip title="Remove from category">
        <span style={{ width: '100%' }}>
          <IconButton
            onClick={onRemove}
            disabled={!canRemove}
            sx={{
              width: 52,
              height: 48,
              borderRadius: 0,
              bgcolor: canRemove ? ACCENT_LIGHT : 'grey.100',
              color: canRemove ? ACCENT : 'grey.400',
              '&:hover': { bgcolor: canRemove ? 'rgba(112, 40, 218, 0.18)' : 'grey.100' },
            }}
          >
            <ArrowBack />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  </Stack>
);

const ProductCategory = () => {
  const dispatch = useDispatch();
  const category = useSelector((state) => state?.productCategory?.categoryData?.data);

  const [budObject, setBudObject] = useState({});
  const [products, setProducts] = useState([]);
  const [includedProducts, setIncludedProducts] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedIncludedProduct, setSelectedIncludedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [permissions, setPermissions] = useState({});
  const [viewProduct, setViewProduct] = useState(null);

  useEffect(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error('Error parsing stored permissions', error);
      }
    }
  }, []);

  const canCreate = canShowPermission(permissions, 'productCategory', 'create');
  const canDelete = canShowPermission(permissions, 'productCategory', 'delete');

  const sortedProducts = useMemo(() => sortByCreatedAt(products), [products]);
  const sortedIncludedProducts = useMemo(() => sortByCreatedAt(includedProducts), [includedProducts]);

  useEffect(() => {
    dispatch(fetchProductCategoryRequest({ budid: '' }));
  }, [dispatch]);

  useEffect(() => {
    if (category?.bud_object) {
      const bud = category.bud_object;
      setBudObject(bud);
      setImagePreview(bud.image || '');
      setIsLive(!!bud.is_live);
      setSelectedCategory(bud);

      const fullProducts = category?.product_object?.filter(Boolean) || [];
      const included =
        bud.products?.map((id) => {
          return fullProducts.find((p) => p._id === id) || { _id: id, name: 'Unknown Product' };
        }) || [];
      const remaining = fullProducts.filter((p) => !included.some((ip) => ip._id === p._id));

      setIncludedProducts(sortByCreatedAt(included));
      setProducts(sortByCreatedAt(remaining));
    } else if (category?.product_object) {
      setProducts(sortByCreatedAt(category.product_object.filter(Boolean)));
    }
  }, [category]);

  const handleFileChange = (files) => {
    if (files.length > 0) {
      const file = files[0];
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmitBud = async () => {
    const formData = new FormData();
    formData.append('oba_bud_id', budObject?._id || '');
    formData.append('oba_bud_name', budObject?.name || '');
    formData.append('oba_bud_is_live', isLive ? 'true' : 'false');

    includedProducts.forEach((product) => {
      if (product && product._id) {
        formData.append('oba_bud_final_products', product._id);
      }
    });

    if (image) {
      formData.append('file', image);
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/product/createOrUpdateBud`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Saved successfully');

      if (budObject?._id) {
        dispatch(fetchProductCategoryRequest({ budid: budObject._id }));
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to save');
    }
  };

  const handleAddProduct = () => {
    const selected = products.find((p) => p._id === selectedProduct);
    if (selected && !includedProducts.some((ip) => ip._id === selected._id)) {
      setIncludedProducts((prev) => sortByCreatedAt([...prev, selected]));
      setProducts((prev) => sortByCreatedAt(prev.filter((p) => p._id !== selected._id)));
      setSelectedProduct(null);
    }
  };

  const handleRemoveProduct = () => {
    const selected = includedProducts.find((p) => p._id === selectedIncludedProduct);
    if (selected) {
      setProducts((prev) => sortByCreatedAt([...prev, selected]));
      setIncludedProducts((prev) => sortByCreatedAt(prev.filter((p) => p._id !== selected._id)));
      setSelectedIncludedProduct(null);
    }
  };

  const handleSelectBud = (bud) => {
    if (bud?._id) {
      dispatch(fetchProductCategoryRequest({ budid: bud._id }));
      setSelectedCategory(bud);
    }
  };

  const categoryName = selectedCategory?.name || budObject?.name;
  const hasSelectedCategory = Boolean(categoryName);

  return (
    <Box sx={{ display: 'flex', background: '#f3f3fd', minHeight: '100vh', width: '100%' }}>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, mt: 0, width: '100%', overflowY: 'auto', maxHeight: '100vh' }}
      >
        <CategoryTreeView
          categoryHierarchy={category?.categoryHierarchy || []}
          onSelect={handleSelectBud}
        />

        <Card elevation={3} sx={{ p: { xs: 2, md: 2.5 }, width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
            <Typography fontFamily="Aeonik" sx={{ fontSize: '22px', fontWeight: 700 }}>
              Manage Category
            </Typography>
            {categoryName && (
              <Chip label={categoryName} sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 600 }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {hasSelectedCategory
              ? 'Update details, assign products, and save.'
              : 'Select a category from the tree above to begin.'}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2,
              opacity: hasSelectedCategory ? 1 : 0.45,
              pointerEvents: hasSelectedCategory ? 'auto' : 'none',
            }}
          >
            <Paper variant="outlined" sx={{ flex: 1.2, p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
              <TextField
                fullWidth
                size="small"
                label="Category Name"
                value={budObject.name || ''}
                onChange={(e) => setBudObject({ ...budObject, name: e.target.value })}
                sx={{ mb: 2.5 }}
              />

              <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                Live
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Visible to customers in the app
              </Typography>
              <RadioGroup
                row
                value={isLive ? 'true' : 'false'}
                onChange={(e) => setIsLive(e.target.value === 'true')}
                sx={{ mb: 2.5 }}
              >
                <FormControlLabel value="true" control={<Radio size="small" color="success" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Products
                  </Typography>
                  <Chip
                    label={`${sortedIncludedProducts.length} in category`}
                    size="small"
                    sx={{ bgcolor: ACCENT_LIGHT, color: ACCENT, fontWeight: 600 }}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
                  <ProductListPanel
                    title="Available Products"
                    count={sortedProducts.length}
                    emptyMessage="All products are assigned"
                    products={sortedProducts}
                    selectedId={selectedProduct}
                    onSelect={setSelectedProduct}
                    onView={setViewProduct}
                    variant="available"
                  />

                  <TransferControls
                    onAdd={handleAddProduct}
                    onRemove={handleRemoveProduct}
                    canAdd={Boolean(selectedProduct)}
                    canRemove={Boolean(selectedIncludedProduct)}
                  />

                  <ProductListPanel
                    title="In Category"
                    subtitle={categoryName ? `Shown in ${categoryName}` : undefined}
                    count={sortedIncludedProducts.length}
                    emptyMessage="No products assigned"
                    products={sortedIncludedProducts}
                    selectedId={selectedIncludedProduct}
                    onSelect={setSelectedIncludedProduct}
                    onView={setViewProduct}
                    variant="included"
                  />
                </Stack>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                flex: 0.7,
                p: 2,
                borderRadius: 2,
                bgcolor: '#fafafa',
                borderColor: imagePreview ? ACCENT_BORDER : 'divider',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                Category Image
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                PNG or JPG, max 5 MB
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: '#fff',
                  border: '1px dashed',
                  borderColor: imagePreview ? ACCENT_BORDER : 'grey.300',
                  minHeight: 160,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!imagePreview ? (
                  <>
                    <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <FileUploader
                      title="Upload Image"
                      maxFiles={1}
                      maxSize={5 * 1024 * 1024}
                      acceptedFileTypes={{ 'image/*': ['.png', '.jpg'] }}
                      onUpload={() => {}}
                      onChange={handleFileChange}
                    />
                  </>
                ) : (
                  <>
                    <img
                      src={imagePreview}
                      alt="Category"
                      style={{ objectFit: 'contain', maxHeight: 220, maxWidth: '100%', borderRadius: 8 }}
                    />
                    {canDelete && (
                      <Button
                        variant="text"
                        onClick={() => {
                          setImage(null);
                          setImagePreview('');
                        }}
                        color="error"
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        Remove
                      </Button>
                    )}
                  </>
                )}
              </Box>

              {canCreate && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Save />}
                  onClick={handleSubmitBud}
                  sx={{
                    mt: 2,
                    textTransform: 'none',
                    bgcolor: ACCENT,
                    fontWeight: 600,
                    py: 1.2,
                    '&:hover': { bgcolor: '#5a1fb0' },
                  }}
                >
                  Save
                </Button>
              )}
            </Paper>
          </Box>
        </Card>

        <ProductDetailModal
          open={Boolean(viewProduct)}
          onClose={() => setViewProduct(null)}
          product={viewProduct}
          categoryName={categoryName}
        />
      </Box>
    </Box>
  );
};

export default ProductCategory;
